import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { errorAlert } from "../../../utils/alert";

/*
  One screen, used for both blog categories and gallery albums.

  They are the same thing with different labels - a name and a live/off
  switch - so they share a component rather than existing twice with the
  fields renamed.

  Deleting never takes the content with it: posts lose their category, photos
  lose their album, and both stay where they are.
*/
const CategoryManager = ({
  title,
  countLabel,
  countKey,
  noun = "category",
  load,
  save,
  remove,
}) => {
  const dispatch = useDispatch();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const data = await dispatch(load());
    setList(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [dispatch, load]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return errorAlert("Please write a name.");

    setBusy(true);
    const body = new FormData();
    body.append("name", form.name.trim());
    body.append("is_active", form.is_active);

    const done = await dispatch(save(body, form.id));
    setBusy(false);

    if (done) {
      setForm(null);
      refresh();
    }
  };

  const del = async (row) => {
    if (
      !window.confirm(
        `Delete "${row.name}"? Anything in it is kept, but it will no longer belong to this ${noun}.`
      )
    ) {
      return;
    }
    const done = await dispatch(remove(row.id));
    if (done) refresh();
  };

  return (
    <div>
      <div class="d-flex justify-content-between align-items-center">
        <div class="BigTitle">{title}</div>
        <button
          type="button"
          class="btn btn-comman"
          onClick={() =>
            setForm({ name: "", is_active: 1 })
          }
        >
          Add New
        </button>
      </div>

      <div class="table-responsive mt-3">
        <table class="table">
          <thead>
            <tr>
              <th>Sr No.</th>
              <th>Name</th>
              <th>{countLabel}</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5}>Loading...</td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={5}>Nothing added yet.</td>
              </tr>
            ) : (
              list.map((row, i) => (
                <tr key={row.id}>
                  <td>{i + 1}</td>
                  <td>{row.name}</td>
                  <td>{row[countKey] ?? 0}</td>
                  <td>
                    {Number(row.is_active) === 1 ? (
                      <span class="text-success">Active</span>
                    ) : (
                      <span class="text-muted">Hidden</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary mr-1"
                      onClick={() =>
                        setForm({
                          id: row.id,
                          name: row.name,
                          is_active: Number(row.is_active),
                        })
                      }
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-danger"
                      onClick={() => del(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal show={!!form} onHide={() => setForm(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{form?.id ? "Edit" : "Add"}</Modal.Title>
        </Modal.Header>
        {form && (
          <form onSubmit={submit}>
            <Modal.Body>
              <div class="mb-2">
                <label>Name *</label>
                <input
                  type="text"
                  class="form-control"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>

              <div class="mb-2">
                <label>Status</label>
                <select
                  class="form-control"
                  value={form.is_active}
                  onChange={(e) =>
                    setForm({ ...form, is_active: Number(e.target.value) })
                  }
                >
                  <option value={1}>Active</option>
                  <option value={0}>Hidden from the website</option>
                </select>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                class="btn btn-secondary"
                onClick={() => setForm(null)}
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-comman" disabled={busy}>
                {busy ? "Saving..." : "Save"}
              </button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default CategoryManager;
